import * as React from "react";
import { authTextFieldStyles } from "../../utils/Styles";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { ILogin } from "../../vm";
import {
  isTokenExpired,
  setToken,
  setTokens,
  getQueryParams,
  parseJwt,
  // passwordUpdatedDaysAgo, — 🚫 PASSWORD EXPIRY
  isValidPassword,
  isAdminUser,
} from "../../services/UtilService";
import { withToastContext, IToast } from "../common/ToastProvider";
import { mdiArrowRight } from "@mdi/js";
import Icon from "@mdi/react";
import { Formik, FormikErrors } from "formik";
import {
  TextField,
  Stack,
  Text,
  PrimaryButton,
  Label,
  IIconProps,
  Link,
} from "office-ui-fabric-react";
import { EMAIL_REGEX } from "../common/Constants";
import {
  loginToApp,
  changeExpiredPasswordForAdmin,
  changeExpiredPasswordForTeam,
  loginToAppWithMSOffice,
} from "../../services/auth/AuthService";
import "./login.css";
import {
  IPasswordChangeAlert,
  withPasswordChangeAlertContext,
} from "../change-password/PasswordChangeAlertProvider";
import PasswordPolicy from "../common/PasswordPolicy";
import LoginLeftSideImage from "../../images/login-image.png";
import LoginPageBg from "../../images/login-page-bg.png";
import {
  msalInstance,
  ensureMsalInitialized,
  msLoginRequest,
  MS_SSO_SUPPRESS_KEY,
  MS_SSO_LOGGED_OUT_PARAM,
  MS_SSO_MODE_KEY,
  MS_SSO_EMERGENCY_PARAM,
} from "../../authConfig";
import { AuthenticationResult } from "@azure/msal-browser";

// 🚫 FORGOT PASSWORD — disabled by client request (SSO rollout).
// Self-serve password reset is no longer offered; the emergency form points
// users at their IT support instead. The component and the backend OTP
// endpoints are all still in place, so re-enabling this is just a matter of
// uncommenting the four blocks marked "FORGOT PASSWORD" in this file.
// Do not delete them.
// import ForgotPassword from "./ForgotPassword";

/**
 * Microsoft's existing monochrome white 2×2 mark on the brand-purple primary
 * button. Pure CSS, no image.
 */
const MicrosoftMark = () => (
  <span className="ms-mark" aria-hidden="true">
    <span />
    <span />
    <span />
    <span />
  </span>
);

/**
 * Drops `?emergency=1` from the address bar without reloading.
 *
 * The param seeds the emergency view on load, so it must not survive a return
 * to the Microsoft card. MSAL's `navigateToLoginRequestUrl` defaults to true:
 * after Microsoft redirects back, MSAL sends the browser to whatever URL
 * `loginRedirect()` was called from. If that URL still carried `?emergency=1`,
 * the break-glass branch — which deliberately runs BEFORE MSAL is touched —
 * would short-circuit and silently discard the ID token, dumping the user back
 * on the password form instead of signing them in. It also stops a plain
 * reload from bouncing back to the emergency form.
 */
const clearEmergencyParam = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(MS_SSO_EMERGENCY_PARAM)) return;
  url.searchParams.delete(MS_SSO_EMERGENCY_PARAM);
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
};

export interface LoginMainProps
  extends RouteComponentProps,
    IToast,
    IPasswordChangeAlert {}

export interface LoginMainState {
  showPasswordUpdateModel?: boolean;
  token?: string;
  refreshToken?: string;
  // 🚫 FORGOT PASSWORD — disabled (see the import note at the top of this file).
  // showForgotPasswordModel?: boolean;
  // While true, render nothing so the login form never flashes before a
  // successful silent Microsoft SSO redirects the user away.
  isBootstrapping?: boolean;
  // Which sign-in path the card is showing. Microsoft is the default and the
  // only promoted option; 'emergency' reveals the email + password form, which
  // exists solely as a fallback for when Microsoft sign-in is unavailable.
  mode?: "sso" | "emergency";
  // Persistent, in-card copy of the last Microsoft sign-in failure. A toast
  // alone is easy to miss when the user has just been bounced back from a
  // full-page Microsoft redirect.
  ssoError?: string | null;
}

class LoginMain extends React.Component<LoginMainProps, LoginMainState> {
  // Guards against React.StrictMode double-invocation in dev.
  private ssoStarted = false;

  constructor(props: LoginMainProps) {
    super(props);
    this.state = {
      showPasswordUpdateModel: false,
      token: undefined,
      refreshToken: undefined,
      isBootstrapping: true,
      // Seeded from `?emergency=1` so the break-glass URL lands straight on the
      // form. (No flash risk: the first render is the null bootstrap state.)
      mode: new URLSearchParams(window.location.search).has(
        MS_SSO_EMERGENCY_PARAM
      )
        ? "emergency"
        : "sso",
      ssoError: null,
    };
  }

  componentDidMount = async () => {
    if (this.ssoStarted) return;
    this.ssoStarted = true;

    // Already signed in → straight through.
    if (!isTokenExpired()) {
      this.props.history.push("/");
      // 🚫 PASSWORD EXPIRY — await this.shouldShowPasswordChangeNotification();
      return;
    }

    // Break-glass (`?emergency=1`) → the password form, immediately.
    // Checked BEFORE MSAL is touched: during a Microsoft outage anything that
    // reaches out to Microsoft may hang, which is exactly when this path has to
    // work. Also sets the suppress flag so reloading this tab doesn't then
    // bounce off to Microsoft.
    if (new URLSearchParams(window.location.search).has(MS_SSO_EMERGENCY_PARAM)) {
      sessionStorage.setItem(MS_SSO_SUPPRESS_KEY, "1");
      this.setState({ mode: "emergency", isBootstrapping: false });
      return;
    }

    try {
      await this.runSsoBootstrap();
    } catch (error) {
      console.error("SSO bootstrap failed:", error);
      this.setState({ isBootstrapping: false });
    }
  };

  // Deterministic login-page state machine for Microsoft SSO (redirect flow).
  runSsoBootstrap = async () => {
    await ensureMsalInitialized();

    const mode = sessionStorage.getItem(MS_SSO_MODE_KEY);

    // 1. Are we returning from a Microsoft redirect?
    let result = null;
    try {
      result = await msalInstance.handleRedirectPromise();
    } catch (err) {
      // A silent probe that actually needed interaction (no live MS session)
      // lands here. Suppress further probes and show the form.
      sessionStorage.removeItem(MS_SSO_MODE_KEY);
      if (mode === "silent") {
        sessionStorage.setItem(MS_SSO_SUPPRESS_KEY, "1");
      } else {
        this.props.showToast("Microsoft sign-in failed. Please try again.", "error");
      }
      this.setState({ isBootstrapping: false });
      return;
    }

    if (result && result.idToken) {
      if (mode) {
        sessionStorage.removeItem(MS_SSO_MODE_KEY);
        const idToken = await this.ensureFreshIdToken(result);
        await this.completeMsLogin(idToken, mode !== "interactive");
        return;
      }
      // A result with no silent/interactive marker means no redirect is
      // actually in flight: MSAL memoizes the redirect result for the lifetime
      // of the document, so reaching /login via an SPA route change (e.g.
      // AuthenticatedRoute's <Redirect> on session expiry) replays the ORIGINAL
      // login's result — a stale, likely expired ID token the backend would
      // reject. Ignore it and fall through to a fresh silent probe.
      console.warn("Ignoring memoized MSAL redirect result — no auth redirect in flight.");
    }

    // 2. Explicit logout, or a previously-failed silent probe → just show the form.
    const params: any = getQueryParams(window.location.href);
    const loggedOut = params[MS_SSO_LOGGED_OUT_PARAM];
    const suppressed = sessionStorage.getItem(MS_SSO_SUPPRESS_KEY);
    if (loggedOut || suppressed) {
      this.setState({ isBootstrapping: false });
      return;
    }

    // 3. Otherwise probe silently using the browser's Microsoft session
    //    (App Launcher case). Navigates away; if it succeeds we come back at (1).
    sessionStorage.setItem(MS_SSO_MODE_KEY, "silent");
    await msalInstance.loginRedirect({ ...msLoginRequest, prompt: "none" as const });
  };

  // Entra ID can return an ID token whose exp is anchored to the browser
  // session's ORIGINAL sign-in (ID tokens have a static ~60-min lifetime), so a
  // silent re-login more than an hour after first sign-in may come back already
  // expired even though it arrived fresh off the network — the backend would
  // reject it with TokenExpiredError. A refresh-token grant mints an ID token
  // with a fresh exp, so force one when needed.
  // See AzureAD/microsoft-authentication-library-for-js#4206.
  ensureFreshIdToken = async (result: AuthenticationResult): Promise<string> => {
    const claims: any = result.idTokenClaims || {};
    const skewMs = 60 * 1000; // refresh when expired or about to expire
    if (!claims.exp || claims.exp * 1000 > Date.now() + skewMs || !result.account) {
      return result.idToken;
    }
    try {
      const refreshed = await msalInstance.acquireTokenSilent({
        ...msLoginRequest,
        account: result.account,
        forceRefresh: true,
      });
      return refreshed.idToken;
    } catch (err) {
      console.error("Forced ID token refresh failed, using original token:", err);
      return result.idToken; // backend rejection then follows the normal failure path
    }
  };

  // Exchange the Microsoft ID token for this app's session.
  completeMsLogin = async (idToken: string, silent: boolean) => {
    const res = await loginToAppWithMSOffice(idToken);
    if (res?.success && res.data?.token) {
      sessionStorage.removeItem(MS_SSO_SUPPRESS_KEY);
      const tokenData = parseJwt(res.data.token);
      if (res.data.refreshToken) {
        setTokens(res.data.token, res.data.refreshToken);
      } else {
        setToken(res.data.token);
      }

      let params: any = getQueryParams(window.location.href);
      if (params["redirect_to"]) {
        this.props.history.push(params["redirect_to"]);
      } else if (
        tokenData.isAdmin === true &&
        (tokenData.role === 0 ||
          tokenData.role === 2 ||
          tokenData.role === 3 ||
          tokenData.role === 4 ||
          tokenData.role === 5)
      ) {
        this.props.history.push("/");
      } else {
        this.props.history.push(`/datacenters`);
      }
      return;
    }

    // Backend rejected (e.g. no matching account / disabled). Show the form +
    // reason; suppress so a silent probe doesn't immediately loop.
    if (silent) sessionStorage.setItem(MS_SSO_SUPPRESS_KEY, "1");
    const message = res?.message || "Unable to sign in with Microsoft.";
    this.props.showToast(message, "error");
    this.setState({ isBootstrapping: false, ssoError: message });
  };

  // "Sign in with Microsoft" button — interactive (shows the account picker).
  handleMicrosoftLogin = async () => {
    this.setState({ ssoError: null });
    // Must happen BEFORE loginRedirect: MSAL returns the browser to the URL the
    // redirect started from, and `?emergency=1` there would make the break-glass
    // branch skip the token exchange on the way back. See clearEmergencyParam.
    clearEmergencyParam();
    try {
      await ensureMsalInitialized();
      // The user is explicitly asking to sign in — lift any suppression left by
      // an earlier failed silent probe.
      sessionStorage.removeItem(MS_SSO_SUPPRESS_KEY);
      sessionStorage.setItem(MS_SSO_MODE_KEY, "interactive");
      await msalInstance.loginRedirect(msLoginRequest);
    } catch (err) {
      sessionStorage.removeItem(MS_SSO_MODE_KEY);
      this.props.showToast("Microsoft sign-in failed. Please try again.", "error");
    }
  };
  // 🚫 PASSWORD EXPIRY — the post-login "your password expires in N days" alert
  // is DISABLED. Full rationale at the mount site, App.tsx.
  //
  // This method is the trigger; the dialog it summoned is unmounted there. The
  // endpoint behind passwordUpdatedDaysAgo (GET last-password-updated-time) is
  // commented out on the API too, so re-enabling this alone would only error.
  // shouldShowPasswordChangeNotification = async () => {
  //   const days = await passwordUpdatedDaysAgo();
  //   // this.props.showPasswordChangeAlert({ isPolicyChangedUpdate: true });
  //   // this.props.showPasswordChangeAlert({ expireId: 14 });
  //   if (days !== null) {
  //     if (45 - days <= 15) {
  //       this.props.showPasswordChangeAlert({ expireId: 45 - days });
  //     }
  //   }
  //   if (days === null) {
  //     this.props.showPasswordChangeAlert({ isPolicyChangedUpdate: true });
  //   }
  // };

  render() {
    if (this.state.isBootstrapping) return null;
    return (
      <React.Fragment>
        <main>
          <div className="left-section">
            {/* <div className="left-img-container"> */}
            <img src={LoginLeftSideImage} alt="" className="left-img" />
            {/* </div> */}
          </div>
          <div className="right-section">
            <div className="login-logo">
              <img
                src={"/logo.png"}
                alt="STTGDC"
                style={{ height: 'auto', width: 210, maxWidth: '100%' }}
              />
            </div>
            <div className="login-card-section">
              {this.state.showPasswordUpdateModel ? (
                <section className="card login-card">
                  <Stack horizontalAlign="start">
                    <Text className="sign-in-text">Customer MOR</Text>
                    <Text className="login-subtitle">Update your Password</Text>
                  </Stack>

                  <Formik
                    initialValues={{ newPassword: "", confirmPassword: "" }}
                    validate={(values) => {
                      let errors: FormikErrors<{
                        newPassword?: string;
                        confirmPassword?: string;
                      }> = {};

                      if (!values.newPassword) {
                        errors.newPassword = "Required";
                      }
                      if (
                        values.newPassword &&
                        !isValidPassword(
                          values.newPassword,
                          isAdminUser(this.state.token)
                        )
                      ) {
                        errors.newPassword = "Please enter valid password.";
                      }
                      if (!values.confirmPassword) {
                        errors.confirmPassword = "Required";
                      }
                      if (
                        values.newPassword &&
                        values.confirmPassword &&
                        values.newPassword !== values.confirmPassword
                      ) {
                        errors.confirmPassword = "Password does not match";
                      }
                      return errors;
                    }}
                    onSubmit={async (
                      values: { newPassword: string; confirmPassword: string },
                      { setSubmitting, setFieldValue }
                    ) => {
                      const tokenData = parseJwt(this.state.token);
                      let result;
                      if (tokenData.isAdmin) {
                        result = await changeExpiredPasswordForAdmin(
                          values.newPassword,
                          this.state.token
                        );
                      } else {
                        result = await changeExpiredPasswordForTeam(
                          values.newPassword,
                          this.state.token
                        );
                      }
                      if (result && result.success) {
                        if (this.state.token && this.state.refreshToken) {
                          setTokens(this.state.token, this.state.refreshToken);
                        } else if (this.state.token) {
                          setToken(this.state.token);
                        }
                        this.props.showToast(
                          "Password updated successfully",
                          "success"
                        );
                        const params: any = getQueryParams(
                          window.location.href
                        );
                        console.log(
                          "params['redirect_to']",
                          params["redirect_to"]
                        );
                        if (params["redirect_to"]) {
                          this.props.history.push(params["redirect_to"]);
                        } else {
                          this.props.history.push("/");
                        }
                      } else {
                        this.props.showToast(result.message, "error");
                      }
                      setSubmitting(false);
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isSubmitting,
                      /* and other goodies */
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <Label className="login-label">New Password</Label>
                        <TextField
                          id="newPassword"
                          name="newPassword"
                          // label="New Password"
                          autoComplete="new-password"
                          type="password"
                          onChange={handleChange}
                          errorMessage={
                            errors.newPassword && touched.newPassword
                              ? errors.newPassword
                              : ""
                          }
                          onBlur={handleBlur}
                          value={values.newPassword}
                          underlined
                          styles={authTextFieldStyles}
                        />
                        {errors.newPassword &&
                          touched.newPassword &&
                          values.newPassword && (
                            <PasswordPolicy
                              password={values.newPassword}
                              isAdmin={isAdminUser(this.state.token)}
                            />
                          )}
                        <Label className="login-label">Confirm Password</Label>
                        <TextField
                          id="confirmPassword"
                          name="confirmPassword"
                          // label="Confirm Password"
                          autoComplete="confirm-password"
                          type="password"
                          onChange={handleChange}
                          errorMessage={
                            errors.confirmPassword && touched.confirmPassword
                              ? errors.confirmPassword
                              : ""
                          }
                          onBlur={handleBlur}
                          value={values.confirmPassword}
                          underlined
                          styles={authTextFieldStyles}
                        />
                        <br />

                        <PrimaryButton
                          type="submit"
                          disabled={isSubmitting}
                          className="login-btn"
                        >
                          Update Password
                        </PrimaryButton>
                      </form>
                    )}
                  </Formik>
                </section>
              ) : this.state.mode === "sso" ? (
                /* ------------------- MICROSOFT SSO (default) ------------------- */
                <section className="card login-card">
                  <Stack horizontalAlign="start">
                    <Text className="sign-in-text">Customer MOR</Text>
                    <Text className="login-subtitle">Sign in to continue</Text>
                  </Stack>

                  {this.state.ssoError && (
                    <div className="sso-error">{this.state.ssoError}</div>
                  )}

                  {/* The primary action, so it wears the brand colour — an
                      outline button here would read as "secondary" everywhere
                      else in the app and undersell the one path users are meant
                      to take. The mark is the monochrome white one (see
                      MicrosoftMark), which is what lets it sit on the purple. */}
                  <PrimaryButton
                    className="login-btn ms-signin-btn"
                    onClick={this.handleMicrosoftLogin}
                  >
                    <MicrosoftMark />
                    Sign in with Microsoft
                  </PrimaryButton>

                  <p className="login-helper">
                    Use your STTGDC work account.
                  </p>

                  <div className="plain-divider" />

                  {/* Deliberately neutral grey, not brand purple, so SSO stays the
                      obviously-primary path. */}
                  <p className="login-helper">
                    Can't access Microsoft?{" "}
                    <Link
                      className="emergency-link"
                      onClick={() =>
                        this.setState({ mode: "emergency", ssoError: null })
                      }
                    >
                      Emergency sign-in
                    </Link>
                  </p>
                </section>
              ) : (
                /* ------------------- EMERGENCY (email + password) ------------------- */
                <section className="card login-card">
                  <Link
                    className="back-to-sso"
                    onClick={() => {
                      clearEmergencyParam();
                      this.setState({ mode: "sso" });
                    }}
                  >
                    ← Back to Microsoft sign-in
                  </Link>

                  <Stack horizontalAlign="start">
                    <Text className="sign-in-text">Emergency sign-in</Text>
                  </Stack>

                  {/* Emergency access is a rare, risk-carrying path, so it gets
                      a visually distinct warning rather than the muted grey
                      helper text the Microsoft screen uses. */}
                  <div className="emergency-callout">
                    Use only when Microsoft sign-in is unavailable. Enter the
                    local credentials issued by IT.
                  </div>

                  <Formik
                    initialValues={{ email: "", password: "" }}
                    validate={(values) => {
                      let errors: FormikErrors<ILogin> = {};
                      if (!values.email) {
                        errors.email = "Required";
                      } else if (!EMAIL_REGEX.test(values.email)) {
                        errors.email = "Invalid email address";
                      }
                      if (!values.password) {
                        errors.password = "Required";
                      } else if (values.password.length <= 4) {
                        errors.password = "Needs to be more than 4 characters";
                      }
                      return errors;
                    }}
                    onSubmit={async (values: ILogin, { setSubmitting }) => {
                      let res = await loginToApp(values);
                      if (res.success && res.data.token) {
                        // show toast message if has an active session
                        if (res.data.hasActiveSession) {
                          this.props.showToast(
                            "Login detected. Your previous session has been closed.",
                            "info"
                          );
                        }
                        const tokenData = parseJwt(res.data.token);
                        if (
                          tokenData &&
                          tokenData.passwordUpdatedByAdminAt != null
                        ) {
                          setSubmitting(false);
                          this.setState({
                            showPasswordUpdateModel: true,
                            token: res.data.token,
                            refreshToken: res.data.refreshToken || undefined,
                          });
                          return;
                        }

                        // Store both tokens
                        if (res.data.refreshToken) {
                          setTokens(res.data.token, res.data.refreshToken);
                        } else {
                          setToken(res.data.token); // Fallback for backward compatibility
                        }
                        // check if needs to show password change notification
                        // 🚫 PASSWORD EXPIRY — await this.shouldShowPasswordChangeNotification();

                        let params: any = getQueryParams(window.location.href);
                        if (params["redirect_to"]) {
                          this.props.history.push(params["redirect_to"]);
                        } else {
                          if (
                            tokenData.isAdmin === true &&
                            (tokenData.role === 0 ||
                              tokenData.role === 2 ||
                              tokenData.role === 3 ||
                              tokenData.role === 4 ||
                              tokenData.role === 5)
                          ) {
                            this.props.history.push("/");
                          } else {
                            this.props.history.push(
                              `/datacenters`
                              // `/datacenters/${tokenData.datacenterId}`
                            );
                          }
                        }
                      } else {
                        this.props.showToast(res?.message, "error");
                      }
                      setSubmitting(false);
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isSubmitting,
                      /* and other goodies */
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <Label className="login-label">Email Address</Label>
                        <TextField
                          id="email"
                          name="email"
                          autoComplete="email"
                          placeholder="you@sttgdc.in"
                          autoFocus
                          onChange={handleChange}
                          errorMessage={
                            errors.email && touched.email ? errors.email : ""
                          }
                          onBlur={handleBlur}
                          value={values.email}
                          underlined
                          styles={authTextFieldStyles}
                        />
                        <Label className="login-label">Password</Label>
                        <TextField
                          id="password"
                          name="password"
                          type="password"
                          autoComplete="current-password"
                          placeholder="••••••••"
                          onChange={handleChange}
                          errorMessage={
                            errors.password && touched.password
                              ? errors.password
                              : ""
                          }
                          onBlur={handleBlur}
                          value={values.password}
                          underlined
                          styles={authTextFieldStyles}
                        />
                        <br />
                        <PrimaryButton
                          type="submit"
                          disabled={isSubmitting}
                          className="login-btn"
                        >
                          <Icon
                            path={mdiArrowRight}
                            size="18px"
                            className="btn-arrow"
                          />
                          Sign in
                        </PrimaryButton>
                        {/* 🚫 FORGOT PASSWORD — disabled (see the import note at
                            the top of this file). Replaced by the "contact your
                            IT support" line below, which is the recovery route
                            now that self-serve reset is off.
                        <div style={{ display: "flex", justifyContent: "end" }}>
                          <Link
                            className="ms-Button-label forgot-password-btn"
                            disabled={isSubmitting}
                            onClick={() =>
                              this.setState({ showForgotPasswordModel: true })
                            }
                          >
                            Forgot Password?
                          </Link>
                        </div>
                        */}
                        <p className="login-helper">
                          Password issues? Contact your IT support.
                        </p>
                      </form>
                    )}
                  </Formik>
                </section>
              )}
              <div className="login-bg-image" aria-hidden="true">
                <img src={LoginPageBg} alt="" />
              </div>
            </div>
          </div>
          {/* 🚫 FORGOT PASSWORD — disabled (see the import note at the top of
              this file). Kept here so re-enabling is a pure uncomment.
          {this.state.showForgotPasswordModel && (
            <ForgotPassword
              onClose={() => this.setState({ showForgotPasswordModel: false })}
            />
          )}
          */}
        </main>
      </React.Fragment>
    );
  }
}

export default withToastContext(
  withPasswordChangeAlertContext(withRouter(LoginMain))
);
